import {
  SAD_PATH_SLOTS_ROW_LIMIT,
  SLOT_ROW_HEIGHT_PX,
  SLOTS_ROW_LIMIT_V3,
} from 'constants/common'
import useViewportHeight from 'hooks/useViewportHeight'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

const STICKY_BOTTOM_SAFETY_MARGIN_PX = 24
// The profile page animates the card's actual `top` between this value (navbar
// visible) and a smaller one (navbar hidden on scroll) — see stickyTopPositionStyle
// in [slug].tsx. Always sizing against this worst case (navbar visible) keeps the
// pin/row-count decision stable; sizing against the live animated value made it
// flip every time the navbar hid/showed, which flickered on borderline (13") screens.
const STICKY_TOP_PX = 160 // matches booking_section_container's static `top: 160px`

type DynamicSlotRowsAndSticky = {
  cardRef: React.RefObject<HTMLDivElement>
  visibleSlotRows: number
  isStickyFeasible: boolean
}

// Only the PrimaryBookButton V2 layout adds enough extra height to the card to
// risk overflowing short viewports (e.g. a 13" laptop) — legacy layout is untouched.
const useDynamicSlotRowsAndSticky = (
  isEnabled: boolean
): DynamicSlotRowsAndSticky => {
  const cardRef = useRef<HTMLDivElement>(null)
  const [cardHeight, setCardHeight] = useState(0)
  const [visibleSlotRows, setVisibleSlotRows] = useState(SLOTS_ROW_LIMIT_V3)
  const viewportHeight = useViewportHeight()

  useLayoutEffect(() => {
    const el = cardRef.current

    if (!isEnabled || !el || typeof ResizeObserver === 'undefined') {
      return
    }

    const measure = () => setCardHeight(el.getBoundingClientRect().height)
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(el)

    return () => observer.disconnect()
  }, [isEnabled])

  const availableHeight =
    viewportHeight - STICKY_TOP_PX - STICKY_BOTTOM_SAFETY_MARGIN_PX
  const chromeHeight = cardHeight - visibleSlotRows * SLOT_ROW_HEIGHT_PX

  useEffect(() => {
    if (!isEnabled || !cardHeight) {
      return
    }

    const desiredRows = Math.min(
      SLOTS_ROW_LIMIT_V3,
      Math.max(
        SAD_PATH_SLOTS_ROW_LIMIT,
        Math.floor((availableHeight - chromeHeight) / SLOT_ROW_HEIGHT_PX)
      )
    )

    setVisibleSlotRows(desiredRows)
  }, [isEnabled, availableHeight, chromeHeight, cardHeight])

  const isStickyFeasible =
    !isEnabled ||
    cardHeight === 0 ||
    chromeHeight + visibleSlotRows * SLOT_ROW_HEIGHT_PX <= availableHeight

  return { cardRef, visibleSlotRows, isStickyFeasible }
}

export default useDynamicSlotRowsAndSticky
